import React, { useEffect, useRef, useState } from 'react'
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { QRCodeCanvas } from 'qrcode.react'
import { asignarCodigosPegatina } from '../../../api/gpsdevice'
import { alertError } from '../../../utils/logger'
import { LOGO_SAILRADAR } from '../../assets/logoSailradar'

/**
 * La hoja de pegatinas QR para pegar en los aparatos.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ PARA QUÉ SIRVE LA PEGATINA                                               │
 * │                                                                          │
 * │ Quien coge el aparato en el pantalán la escanea y ve si está vivo,       │
 * │ cargado, con cobertura y marcando DONDE ESTÁ — no donde estuvo ayer.     │
 * │ Es la comprobación de treinta segundos antes de salir al agua.           │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * EL QR LLEVA EL CÓDIGO DE PEGATINA, NUNCA EL IMEI. El IMEI va impreso en el
 * propio aparato, se repartía en las respuestas públicas y en un pedido viene
 * casi correlativo: con uno se adivinaban los demás, así que «tener el código»
 * no significaba «tener el aparato» y la pantalla acababa siendo un buscador
 * de barcos. El código de pegatina es aleatorio: tenerlo significa haberla
 * tenido delante, y por eso con él SÍ sale la posición.
 *
 * Las pegatinas viejas (julio, con IMEI) siguen abriendo la pantalla y dicen
 * si el aparato está vivo, pero no enseñan el mapa. Reimprimirlas lo devuelve.
 *
 * La hoja se abre en una ventana aparte y no se imprime desde aquí: así el
 * navegador enseña su vista previa, se elige impresora y papel, y sobre todo
 * el backoffice no se queda con media hoja pintada encima si se cancela.
 */

/*
 * LAS MEDIDAS SALEN DEL APARATO, NO DE LA HOJA.
 *
 * El AD50 mide 57 × 39 × 17 mm, así que su cara de arriba son 57 × 39. La
 * pegatina se queda en 52 × 34 para dejar un par de milímetros por lado: una
 * pegatina a ras del borde se empieza a despegar por las esquinas a la
 * primera salpicadura, y estos aparatos viven mojados.
 *
 * Las primeras salieron de 40 mm de QR más cabecera y pie —pensadas para una
 * hoja bonita, no para el aparato— y no cabían.
 */
const ANCHO_MM = 52
const ALTO_MM = 34
/** El QR ocupa casi todo el alto: es lo único que hay que poder escanear. */
const QR_MM = 26

/** Márgenes de la hoja y separación para poder pasar la tijera. */
const MARGEN_HOJA_MM = 10
const SEPARACION_MM = 3

/** Lo que cabe en un A4: (210-20)/55 → 3 columnas, (297-20)/37 → 7 filas. */
const COLUMNAS = Math.floor((210 - 2 * MARGEN_HOJA_MM + SEPARACION_MM) / (ANCHO_MM + SEPARACION_MM))
const FILAS = Math.floor((297 - 2 * MARGEN_HOJA_MM + SEPARACION_MM) / (ALTO_MM + SEPARACION_MM))
const POR_HOJA = COLUMNAS * FILAS

/** Lo que se imprime debajo del QR: la referencia, que es como se llaman. */
function etiquetaDe(aparato) {
	return aparato.reference || aparato.code || 'GPS'
}

/**
 * A un móvil no se le pega nada.
 *
 * Cuando un regatista emite con su teléfono, el servidor le crea un aparato
 * virtual con código `PHONE-<id del regatista>` para que sus posiciones entren
 * como las de cualquier tracker. Existe en la base y sale en el listado, pero
 * no es una cosa: no hay superficie donde pegar una pegatina, y darle código
 * sería gastar uno y ensuciar la hoja.
 */
function esAparatoFisico(aparato) {
	return !String(aparato?.code ?? '').toUpperCase().startsWith('PHONE-')
}

export default function HojaPegatinasDialog({ open, onClose, aparatos = [] }) {
	// Preseleccionados los que NO tienen código: son los recién dados de alta,
	// que es justo para lo que se abre esto cuando llega una caja.
	const [elegidos, setElegidos] = useState(() => new Set())
	const [trabajando, setTrabajando] = useState(false)
	// Los aparatos con su código ya resuelto (los que no tenían, tras pedirlo).
	const [conCodigo, setConCodigo] = useState([])
	/**
	 * La lista con la que trabaja el diálogo: una FOTO tomada al abrirlo.
	 *
	 * Detrás, el listado de aparatos se refresca solo cada pocos segundos y
	 * entrega un array nuevo cada vez. Cuando esto dependía de él, cada refresco
	 * volvía a sembrar la selección: desmarcabas uno, pasaban unos segundos y
	 * aparecía marcado otra vez con todo lo demás desmarcado. Elegir sobre una
	 * lista que se mueve sola no es elegir.
	 */
	const [lista, setLista] = useState([])
	const lienzos = useRef({})

	useEffect(() => {
		if (!open)
			return
		const fisicos = aparatos.filter(esAparatoFisico)
		setLista(fisicos)
		setConCodigo([])
		setElegidos(new Set(fisicos.filter((a) => !a.publicCode).map((a) => a._id)))
		// `aparatos` queda fuera a propósito: esto siembra al ABRIR y no cada vez
		// que el listado de detrás se refresca. Ver el comentario de `lista`.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	const alternar = (id) => {
		setElegidos((prev) => {
			const siguiente = new Set(prev)
			if (siguiente.has(id))
				siguiente.delete(id)
			else
				siguiente.add(id)
			return siguiente
		})
	}

	const seleccionados = lista.filter((a) => elegidos.has(a._id))
	const sinCodigo = seleccionados.filter((a) => !a.publicCode)

	/**
	 * Paso 1: que todos los elegidos tengan código.
	 *
	 * Se pide SOLO para los seleccionados que no lo tengan. El backend no pisa
	 * los que ya tienen, así que repetir esto no puede matar una pegatina que
	 * ya esté pegada en un aparato por ahí.
	 */
	const prepararCodigos = async () => {
		setTrabajando(true)
		try {
			let puestos = []
			if (sinCodigo.length) {
				const res = await asignarCodigosPegatina(sinCodigo.map((a) => a._id))
				puestos = res.data?.puestos ?? []
			}
			const porId = new Map(puestos.map((p) => [String(p._id), p.publicCode]))
			setConCodigo(
				seleccionados.map((a) => ({
					...a,
					publicCode: a.publicCode || porId.get(String(a._id)) || null,
				}))
			)
		} catch (error) {
			alertError({ error, customMessage: 'No se pudieron asignar los códigos de pegatina.' })
		} finally {
			setTrabajando(false)
		}
	}

	/**
	 * Paso 2: la hoja.
	 *
	 * Los QR se pintan aquí en canvas ocultos y se pasan a la ventana como
	 * imágenes ya hechas (`toDataURL`). Así la hoja es HTML plano sin scripts
	 * ni dependencias: se imprime igual dentro de diez años, y se puede guardar
	 * el archivo como respaldo de lo que se imprimió.
	 */
	const imprimir = () => {
		const listos = conCodigo.filter((a) => a.publicCode)
		if (!listos.length)
			return

		const pegatinas = listos
			.map((a) => {
				const lienzo = lienzos.current[a._id]
				const png = lienzo ? lienzo.toDataURL('image/png') : ''
				return `
    <div class="pegatina">
      <img class="qr" src="${png}" alt="QR ${etiquetaDe(a)}" />
      <div class="datos">
        <img class="logo" src="${LOGO_SAILRADAR}" alt="SailRadar" />
        <div class="ref">${etiquetaDe(a)}</div>
        <div class="codigo">${a.publicCode}</div>
      </div>
    </div>`
			})
			.join('')

		const hoja = `<!doctype html>
<html lang="es"><head><meta charset="utf-8">
<title>Pegatinas QR — SailRadar</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; background:#f1f5f9; }
  .barra { padding:16px 24px; display:flex; align-items:center; gap:16px; flex-wrap:wrap; }
  .barra button { background:#123A55; color:#fff; border:0; border-radius:8px; padding:10px 18px; font-size:14px; font-weight:600; cursor:pointer; }
  .barra p { color:#64748b; font-size:13px; max-width:70ch; }
  .hoja { width:210mm; min-height:297mm; margin:0 auto 20px; background:#fff; padding:${MARGEN_HOJA_MM}mm; box-shadow:0 2px 12px rgba(0,0,0,.08); }
  .rejilla { display:grid; grid-template-columns:repeat(${COLUMNAS}, ${ANCHO_MM}mm); gap:${SEPARACION_MM}mm; justify-content:center; }

  /*
   * LA PEGATINA VA MEDIDA AL APARATO: ${ANCHO_MM} x ${ALTO_MM} mm.
   * El AD50 mide 57 x 39 mm de cara, así que quedan un par de milímetros de
   * respiro por lado — pegarla a ras del borde es pedir que se despegue por
   * las esquinas en cuanto le entre agua salada.
   */
  .pegatina {
    width:${ANCHO_MM}mm; height:${ALTO_MM}mm;
    border:1px dashed #cbd5e1; border-radius:2mm;
    padding:2mm; display:flex; align-items:center; gap:2mm;
    break-inside:avoid; overflow:hidden;
  }
  /* El QR manda: es lo único que hay que poder escanear con una mano mojada. */
  .qr { width:${QR_MM}mm; height:${QR_MM}mm; flex:0 0 auto; display:block; }
  .datos { flex:1 1 auto; min-width:0; text-align:center; }
  .logo { width:100%; max-width:19mm; height:auto; display:block; margin:0 auto .8mm; }
  .ref { font-size:11pt; font-weight:800; color:#0F3046; line-height:1.05; }
  /* El código, para poder dictarlo por teléfono un sábado con viento. */
  .codigo { font-size:6.5pt; color:#475569; letter-spacing:.08em; font-family: ui-monospace, 'SF Mono', Menlo, monospace; }

  @media print {
    .barra { display:none; }
    body { background:#fff; }
    .hoja { box-shadow:none; margin:0; }
    @page { size:A4; margin:0; }
  }
</style></head><body>
  <div class="barra">
    <button onclick="window.print()">Imprimir</button>
    <p>A4 &middot; ${listos.length} pegatina(s) de ${ANCHO_MM}&times;${ALTO_MM} mm, ${POR_HOJA} por hoja &middot;
       papel adhesivo, y recorta por la l&iacute;nea discontinua.
       <b>Imprime a tama&ntilde;o real</b>: si el di&aacute;logo de impresi&oacute;n trae
       &laquo;ajustar a la p&aacute;gina&raquo;, qu&iacute;talo o no medir&aacute;n lo que dicen.
       Cada QR abre la pantalla del aparato: bater&iacute;a, cobertura y posici&oacute;n.</p>
  </div>
  <div class="hoja"><div class="rejilla">${pegatinas}</div></div>
</body></html>`

		const ventana = window.open('', '_blank')
		if (!ventana) {
			alertError({
				error: null,
				customMessage: 'El navegador ha bloqueado la ventana. Permite las ventanas emergentes y vuelve a intentarlo.',
			})
			return
		}
		ventana.document.write(hoja)
		ventana.document.close()
	}

	const listosParaImprimir = conCodigo.filter((a) => a.publicCode)

	return (
		<Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				Hoja de pegatinas QR
				<IconButton style={{ position: 'absolute', right: 8, top: 8 }} onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				<p style={{ color: '#7b8794', fontSize: 13, marginBottom: 12 }}>
					Vienen marcados los aparatos que aún no tienen pegatina. A los que les falte
					código se les pondrá uno al preparar la hoja; los que ya lo tengan lo conservan
					— reimprimir una pegatina no invalida la que esté pegada.
				</p>

				{/* Marcar treinta casillas a mano para reimprimir una tanda entera
				    no es trabajo de nadie. */}
				<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
					<Button size="small" onClick={() => setElegidos(new Set(lista.map((a) => a._id)))}>
						Todos
					</Button>
					<Button size="small" onClick={() => setElegidos(new Set())}>
						Ninguno
					</Button>
					{/* El caso de cuando llega una caja: los que aún no tienen. */}
					{lista.some((a) => !a.publicCode) && (
						<Button
							size="small"
							onClick={() =>
								setElegidos(new Set(lista.filter((a) => !a.publicCode).map((a) => a._id)))
							}
						>
							Solo los que no tienen
						</Button>
					)}
				</div>

				<div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #ebedf3', borderRadius: 6, padding: '4px 10px' }}>
					{lista.map((a) => (
						<div key={a._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={elegidos.has(a._id)}
										onChange={() => alternar(a._id)}
										color="primary"
										size="small"
									/>
								}
								label={<span style={{ fontSize: 13 }}>{etiquetaDe(a)}</span>}
							/>
							<span style={{ fontSize: 11, color: a.publicCode ? '#7b8794' : '#F64E60' }}>
								{a.publicCode ? a.publicCode : 'sin código'}
							</span>
						</div>
					))}
					{!lista.length && (
						<p style={{ fontSize: 13, color: '#7b8794', padding: '10px 0' }}>No hay aparatos en la lista.</p>
					)}
				</div>

				<p style={{ marginTop: 12, fontSize: 12, color: '#7b8794' }}>
					{seleccionados.length} seleccionado(s)
					{sinCodigo.length ? ` · ${sinCodigo.length} necesita(n) código` : ''}
					{seleccionados.length > POR_HOJA
						? ` · ocuparán ${Math.ceil(seleccionados.length / POR_HOJA)} hojas A4`
						: ''}
				</p>

				{/*
				  Los QR, de donde salen las imágenes que se pegan en la hoja.
				  Tienen que EXISTIR en el DOM para poder sacarles el PNG, pero no
				  se ven: son de 512 px y taparían el diálogo entero.

				  Una caja de 0×0 con overflow oculto, y no el truco de
				  `position:absolute; left:-10000px` que había: dentro del
				  DialogContent de Material-UI —que trae su propio scroll— aquel
				  absolute se quedaba anclado donde no debía y el QR salía encima
				  del texto. De una caja de tamaño cero no puede asomar nada.
				*/}
				<div style={{ width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
					{listosParaImprimir.map((a) => (
						<QRCodeCanvas
							key={a._id}
							value={`https://live.sailradar.com/device/${a.publicCode}`}
							size={512}
							// «M» corrige hasta un 15% del código dañado: estas
							// pegatinas viven pegadas a un aparato que se moja, se
							// raya y coge sol. Y el margen blanco no es adorno —
							// sin zona de silencio alrededor, muchos lectores no
							// enganchan el código.
							level="M"
							marginSize={2}
							ref={(el) => {
								if (el)
									lienzos.current[a._id] = el
							}}
						/>
					))}
				</div>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cerrar</Button>
				{!listosParaImprimir.length ? (
					<Button
						color="primary"
						variant="contained"
						disabled={!seleccionados.length || trabajando}
						onClick={prepararCodigos}
					>
						{trabajando ? 'Preparando…' : 'Preparar hoja'}
					</Button>
				) : (
					<Button color="primary" variant="contained" onClick={imprimir}>
						Abrir hoja para imprimir ({listosParaImprimir.length})
					</Button>
				)}
			</DialogActions>
		</Dialog>
	)
}
